/**
 * Admin Ops Tickets Page — alle team_handling jobs die handmatig moeten worden geplaatst.
 *
 * Voor klanten op een CMS zonder schrijf-API (Joomla/Drupal/Mijndomein/Hostnet/etc).
 * Ops-specialist ziet hier:
 *  - Klant + CMS + opdracht
 *  - Huidige situatie + Beter voorstel (kant-en-klare AI content)
 *  - SLA timer (5 werkdagen)
 *  - Resolve-formulier (evidence URL + note)
 *
 * Endpoint backend:
 *  - GET /api/admin/ops-tickets
 *  - POST /api/admin/ops-tickets/{ticket_id}/resolve
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Headphones, Clock, CheckCircle2, RefreshCw, ExternalLink,
  AlertTriangle, User, Globe, FileCheck, X,
} from 'lucide-react';
import { toast } from 'sonner';

/* SLA timer helper */
function getSlaInfo(ticket) {
  const created = new Date(ticket.created_at).getTime();
  const slaMs = (ticket.sla_hours || 120) * 3600 * 1000;
  const deadline = created + slaMs;
  const now = Date.now();
  const remainingMs = deadline - now;
  const isOverdue = remainingMs < 0;
  const hours = Math.floor(Math.abs(remainingMs) / 3600 / 1000);
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return {
    isOverdue,
    label: isOverdue
      ? `${days > 0 ? days + 'd ' : ''}${remHours}u OVER TIJD`
      : `Nog ${days > 0 ? days + 'd ' : ''}${remHours}u`,
    color: isOverdue ? '#b91c1c' : (days < 1 ? '#c2410c' : '#15803d'),
  };
}

/* CMS provider mooie labels */
const CMS_LABEL = {
  joomla: 'Joomla', drupal: 'Drupal', typo3: 'TYPO3', opencart: 'OpenCart',
  mijndomein: 'Mijndomein', hostnet: 'Hostnet', combell: 'Combell', versio: 'Versio',
};

function ResolveModal({ ticket, onClose, onResolved }) {
  const { api } = useAuth();
  const [evidenceUrl, setEvidenceUrl] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!evidenceUrl.trim()) {
      toast.error('Evidence URL is vereist (bv. link naar de aangepaste pagina)');
      return;
    }
    setSubmitting(true);
    try {
      await api('post', `/api/admin/ops-tickets/${ticket.ticket_id}/resolve`, {
        evidence_url: evidenceUrl.trim(),
        note: note.trim(),
      });
      toast.success(`Ticket #${ticket.ticket_id} opgelost — klant ziet 'Uitgevoerd' status`);
      onResolved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Resolve mislukt');
    }
    setSubmitting(false);
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div data-testid={`resolve-modal-${ticket.ticket_id}`} onClick={(e) => e.stopPropagation()}
        style={{ background: 'white', borderRadius: 16, maxWidth: 540, width: '100%', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <p style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
              Ticket #{ticket.ticket_id} · {CMS_LABEL[ticket.cms_provider] || ticket.cms_provider}
            </p>
            <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 18, fontWeight: 800, color: '#0f172a', marginTop: 4 }}>
              {ticket.title}
            </h2>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
            <X size={18} />
          </button>
        </div>

        {/* Context: huidige + beter voorstel */}
        {ticket.huidige_situatie && (
          <div style={{ background: '#fef2f2', borderLeft: '3px solid #f87171', borderRadius: '0 6px 6px 0', padding: '10px 12px', marginBottom: 8 }}>
            <p style={{ fontSize: 10, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>Huidig</p>
            <p style={{ fontSize: 12, color: '#0f172a', fontStyle: 'italic', marginTop: 2 }}>&ldquo;{ticket.huidige_situatie}&rdquo;</p>
          </div>
        )}
        {ticket.beter_voorstel && (
          <div style={{ background: '#fff8e1', borderLeft: '3px solid #D4A017', borderRadius: '0 6px 6px 0', padding: '10px 12px', marginBottom: 16 }}>
            <p style={{ fontSize: 10, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>Plaats deze content:</p>
            <p style={{ fontSize: 12, color: '#0f172a', fontWeight: 600, marginTop: 2, whiteSpace: 'pre-wrap' }}>{ticket.beter_voorstel}</p>
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            Evidence URL <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            data-testid="resolve-evidence-url"
            type="url"
            value={evidenceUrl}
            onChange={(e) => setEvidenceUrl(e.target.value)}
            placeholder="https://klantsite.nl/pagina-waar-gewijzigd-is"
            style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            Wat hebt u gedaan? (optioneel)
          </label>
          <textarea
            data-testid="resolve-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Bv. H1 toegevoegd via FTP edit van header.tpl"
            rows={3}
            style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box', resize: 'vertical' }}
          />
        </div>

        <button
          data-testid="resolve-submit"
          onClick={submit}
          disabled={submitting}
          style={{
            width: '100%', padding: 12, borderRadius: 10, border: 'none',
            background: '#15803d', color: 'white', fontSize: 14, fontWeight: 700,
            cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.6 : 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
          {submitting ? <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <CheckCircle2 size={14} />}
          {submitting ? 'Opslaan…' : 'Markeer als uitgevoerd'}
        </button>
      </div>
    </div>
  );
}

function TicketCard({ ticket, onResolve }) {
  const sla = getSlaInfo(ticket);
  const isOpen = ticket.status === 'open';

  return (
    <div data-testid={`ops-ticket-${ticket.ticket_id}`} style={{
      background: 'white', borderRadius: 14, border: '1px solid #e2e8f0',
      padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
            #{ticket.ticket_id} · {CMS_LABEL[ticket.cms_provider] || ticket.cms_provider}
          </p>
          <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 14, fontWeight: 800, color: '#0f172a', marginTop: 2 }}>
            {ticket.title}
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
            <span style={{ fontSize: 11, color: '#64748b', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <User size={11} /> {ticket.user_email || 'onbekende klant'}
            </span>
            <span style={{ fontSize: 11, color: '#64748b', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Clock size={11} /> {new Date(ticket.created_at).toLocaleString('nl-NL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
          {isOpen ? (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
              background: sla.isOverdue ? '#fee2e2' : '#cffafe',
              color: sla.color,
              border: `1px solid ${sla.isOverdue ? '#fca5a5' : '#67e8f9'}`,
            }}>
              {sla.isOverdue ? <AlertTriangle size={11} /> : <Clock size={11} />} {sla.label}
            </span>
          ) : (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
              background: '#dcfce7', color: '#15803d', border: '1px solid #86efac',
            }}>
              <CheckCircle2 size={11} /> Opgelost
            </span>
          )}
        </div>
      </div>

      {ticket.huidige_situatie && (
        <div style={{ background: '#fef2f2', borderLeft: '3px solid #f87171', borderRadius: '0 6px 6px 0', padding: '8px 10px' }}>
          <p style={{ fontSize: 9, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>Huidig</p>
          <p style={{ fontSize: 11, color: '#0f172a', fontStyle: 'italic', marginTop: 2 }}>
            &ldquo;{(ticket.huidige_situatie || '').slice(0, 160)}{ticket.huidige_situatie?.length > 160 ? '…' : ''}&rdquo;
          </p>
        </div>
      )}
      {ticket.beter_voorstel && (
        <div style={{ background: '#fff8e1', borderLeft: '3px solid #D4A017', borderRadius: '0 6px 6px 0', padding: '8px 10px' }}>
          <p style={{ fontSize: 9, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>Plaatsen</p>
          <p style={{ fontSize: 11, color: '#0f172a', fontWeight: 600, marginTop: 2, whiteSpace: 'pre-wrap' }}>
            {(ticket.beter_voorstel || '').slice(0, 240)}{ticket.beter_voorstel?.length > 240 ? '…' : ''}
          </p>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {isOpen ? (
          <button
            data-testid={`resolve-btn-${ticket.ticket_id}`}
            onClick={() => onResolve(ticket)}
            style={{
              background: '#15803d', color: 'white', border: 'none', borderRadius: 8,
              padding: '7px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 5,
            }}>
            <CheckCircle2 size={12} /> Markeer uitgevoerd
          </button>
        ) : (
          ticket.evidence_url && (
            <a
              href={ticket.evidence_url}
              target="_blank" rel="noopener noreferrer"
              style={{
                background: '#0f172a', color: 'white', padding: '7px 14px',
                borderRadius: 8, fontSize: 11, fontWeight: 700, textDecoration: 'none',
                display: 'inline-flex', alignItems: 'center', gap: 5,
              }}>
              <ExternalLink size={12} /> Bekijk live resultaat
            </a>
          )
        )}
      </div>
    </div>
  );
}

export default function OpsTicketsPage() {
  const { api } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');
  const [resolveTarget, setResolveTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter === 'all'
        ? '/api/admin/ops-tickets'
        : `/api/admin/ops-tickets?status=${filter}`;
      const r = await api('get', url);
      setTickets(r.data.tickets || []);
    } catch {
      toast.error('Kon tickets niet laden');
    }
    setLoading(false);
  }, [api, filter]);

  useEffect(() => {
    api('get', filter === 'all' ? '/api/admin/ops-tickets' : `/api/admin/ops-tickets?status=${filter}`)
      .then((r) => setTickets(r.data.tickets || []))
      .catch(() => toast.error('Kon tickets niet laden'))
      .finally(() => setLoading(false));
  }, [filter, api]);

  const openCount = tickets.filter((t) => t.status === 'open').length;
  const overdueCount = tickets.filter((t) => {
    const sla = getSlaInfo(t);
    return t.status === 'open' && sla.isOverdue;
  }).length;

  return (
    <div data-testid="admin-ops-tickets-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
        <div>
          <h1 style={{ fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 800, color: '#f8fafc', marginBottom: 4 }}>
            <Headphones size={18} style={{ display: 'inline', color: '#67e8f9', marginRight: 8, verticalAlign: 'middle' }} />
            Ops Tickets — Handmatige plaatsingen
          </h1>
          <p style={{ color: '#94a3b8', fontSize: 13 }}>
            Klanten op CMS zonder schrijf-API (Joomla/Drupal/Mijndomein/etc) — uw team plaatst handmatig binnen SLA.
          </p>
        </div>
        <button
          data-testid="refresh-btn"
          onClick={load}
          style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
          <RefreshCw size={13} /> Vernieuwen
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 16 }}>
        <div style={{ background: 'white', borderRadius: 12, padding: '12px 14px', border: '1px solid #e2e8f0' }}>
          <p style={{ fontFamily: "'Sora',sans-serif", fontSize: 24, fontWeight: 800, color: '#0e7490' }}>{openCount}</p>
          <p style={{ fontSize: 10, color: '#0e7490', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginTop: 2 }}>Open tickets</p>
        </div>
        <div style={{ background: 'white', borderRadius: 12, padding: '12px 14px', border: `1px solid ${overdueCount > 0 ? '#fca5a5' : '#e2e8f0'}` }}>
          <p style={{ fontFamily: "'Sora',sans-serif", fontSize: 24, fontWeight: 800, color: overdueCount > 0 ? '#b91c1c' : '#15803d' }}>{overdueCount}</p>
          <p style={{ fontSize: 10, color: overdueCount > 0 ? '#b91c1c' : '#15803d', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginTop: 2 }}>Over tijd</p>
        </div>
        <div style={{ background: 'white', borderRadius: 12, padding: '12px 14px', border: '1px solid #e2e8f0' }}>
          <p style={{ fontFamily: "'Sora',sans-serif", fontSize: 24, fontWeight: 800, color: '#15803d' }}>
            {tickets.filter((t) => t.status === 'resolved').length}
          </p>
          <p style={{ fontSize: 10, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginTop: 2 }}>Opgelost</p>
        </div>
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {[
          { v: 'open', l: 'Open' },
          { v: 'resolved', l: 'Opgelost' },
          { v: 'all', l: 'Alles' },
        ].map((f) => (
          <button
            key={f.v}
            data-testid={`filter-${f.v}`}
            onClick={() => setFilter(f.v)}
            style={{
              background: filter === f.v ? '#0f172a' : 'white',
              color: filter === f.v ? 'white' : '#64748b',
              border: '1px solid ' + (filter === f.v ? '#0f172a' : '#e2e8f0'),
              borderRadius: 999, padding: '5px 14px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
            }}>
            {f.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Laden…</div>
      ) : tickets.length === 0 ? (
        <div data-testid="no-tickets" style={{ background: 'white', borderRadius: 12, border: '1px solid #f1f5f9', padding: 40, textAlign: 'center' }}>
          <FileCheck size={28} style={{ color: '#15803d', marginBottom: 12 }} />
          <p style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Geen {filter === 'open' ? 'openstaande' : ''} tickets</p>
          <p style={{ fontSize: 12, color: '#64748b' }}>
            {filter === 'open' ? 'Goed werk — alles is up-to-date.' : 'Probeer een andere filter.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {tickets.map((t) => <TicketCard key={t.ticket_id} ticket={t} onResolve={setResolveTarget} />)}
        </div>
      )}

      {resolveTarget && (
        <ResolveModal
          ticket={resolveTarget}
          onClose={() => setResolveTarget(null)}
          onResolved={() => { setResolveTarget(null); load(); }}
        />
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
